import React, { useMemo, useEffect, useState, useRef, useCallback } from "react";
import ReactPlayer from "react-player";
import { observer } from "mobx-react-lite";

import "./VideoPlayer.scss";
import LoadingSpinner from "../loading-spinner/LoadingSpinner";
import VideoControls from "./VideoControls";
import useTheme from "../../services/theme-service/ThemeService";
import { translationService } from "../../services/translation/TranslationService";
import { videoPlayerService } from "../../services/video-player/VideoPlayerService";
import { VideoAnalysisService } from "../../services/inspection/VideoAnalysisService";
import { featureFlagsStorage } from "../../services/feature-flags/FeatureFlagsStorage";
import { hexToRGBA } from "../../helpers/ColorHelpers";
import { Segmentation } from "../../api-models/response/VideoAnalysisDataResponse";
import { Flags } from "../../generated/FeatureFlags";
import { ConfidenceOptions } from "../../pages/analysis/components/video-analysis-diagram/tools/Tools";

interface VideoPlayerProps {
  videoAnalysis?: VideoAnalysisService;
}

export default observer(function VideoPlayer(props: VideoPlayerProps) {
  const { themeClass } = useTheme();
  const {
    player,
    video,
    isVideoPlaying,
    aVideoIsLoaded: videoWasLoaded,
    isFullscreen,
    handleProgress,
    handleDuration,
    isPictureInPicture,
    cancelDetachVideo,
    videoUrl,
    videoProgress,
    setVideoCanPlay,
    isVideoPositionProgress,
    clearVideoPositionProgress,
    videoPlayerTimestampsLoaded,
    showArea,
    showBox,
    showContour,
  } = videoPlayerService;

  const [videoUrlWithInitialProgress, setVideoUrlWithInitialProgress] = useState("");
  const [videoPlayerWidth, setVideoPlayerWidth] = useState(0);
  const [videoPlayerHeight, setVideoPlayerHeight] = useState(0);
  const requestId = useRef<number>();

  const { videoAnalysis } = props;
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const filteredSegmentations = useMemo(() => {
    if (videoAnalysis?.isLoading || !videoAnalysis?.segmentationWasSuccessful) return [];

    if (
      videoAnalysis?.highlightDetectionConfidenceOption === ConfidenceOptions.Recommended ||
      videoAnalysis?.highlightDetectionConfidenceOption === ConfidenceOptions.Individual
    ) {
      let defaultSegmentations: Segmentation[] = [];

      const confidenceLevels =
        videoAnalysis?.highlightDetectionConfidenceOption === ConfidenceOptions.Recommended
          ? videoAnalysis?.vaData.recommendedConfidenceLevels
          : videoAnalysis?.detectionIndividualConfidenceLevels;

      for (const groupCode in videoAnalysis?.detectedGroupCodes) {
        defaultSegmentations = [
          ...defaultSegmentations,
          ...videoAnalysis?.vaData.segmentationDetails?.segmentations.filter((x) => {
            return (
              x.groupCode === videoAnalysis?.detectedGroupCodes[groupCode].code &&
              x.accuracy === confidenceLevels[videoAnalysis?.detectedGroupCodes[groupCode].code] / (videoAnalysis?.highlightDetectionConfidenceOption === ConfidenceOptions.Recommended ? 1 : 100)
            );
          }),
        ];
      }

      return defaultSegmentations;
    }

    return videoAnalysis?.vaData.segmentationDetails?.segmentations.filter(
      (x) => x.accuracy === videoAnalysis?.highlightDetectionOverallConfidence
    );
  }, [
    videoAnalysis?.detectedGroupCodes,
    videoAnalysis?.detectionIndividualConfidenceLevels,
    videoAnalysis?.highlightDetectionConfidenceOption,
    videoAnalysis?.highlightDetectionOverallConfidence,
    videoAnalysis?.isLoading,
    videoAnalysis?.segmentationWasSuccessful,
    videoAnalysis?.vaData.recommendedConfidenceLevels,
    videoAnalysis?.vaData.segmentationDetails?.segmentations,
  ]);

  const { width: videoWidth, height: videoHeight } = useMemo(() => {
    if (!videoWasLoaded) return { width: 0, height: 0 };
    if (isFullscreen) return { width: "100%", height: "90%" };

    return {
      width: undefined,
      height: undefined,
    };
  }, [videoWasLoaded, isFullscreen]);

  const videoCanPlay = useMemo(
    () => videoWasLoaded && videoPlayerTimestampsLoaded,
    [videoPlayerTimestampsLoaded, videoWasLoaded]
  );

  useEffect(() => {
    if (videoCanPlay || (isVideoPositionProgress && videoCanPlay)) {
      setVideoUrlWithInitialProgress(`${videoUrl}#t=${videoProgress}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoUrl, videoCanPlay, isVideoPositionProgress]);

  useEffect(() => {
    setVideoCanPlay(!!videoCanPlay);
  }, [videoCanPlay, setVideoCanPlay]);

  useEffect(() => {
    return () => {
      setVideoCanPlay(false);
      clearVideoPositionProgress();
    };
  }, [setVideoCanPlay, clearVideoPositionProgress]);

  const renderSegmentationsBasedOnGroupCode = useCallback(
    (segmentations: Segmentation[], context: CanvasRenderingContext2D) => {
      const color = videoAnalysis?.getGroupCodeColors()[segmentations[0].groupCode.toLowerCase()];

      for (let i = 0; i < segmentations.length; i++) {
        context.beginPath();
        context.lineWidth = videoPlayerService.isFullscreen ? 4 : 2;
        context.strokeStyle = hexToRGBA(color);
        context.fillStyle = hexToRGBA(color, 0.2);

        for (let j = 0; j < segmentations[i].coordinates.length; j++) {
          context.lineTo(
            (segmentations[i].coordinates[j].x * videoPlayerWidth) /
              videoAnalysis?.vaData.segmentationDetails.imageSize.width!,
            (segmentations[i].coordinates[j].y * videoPlayerHeight) /
              videoAnalysis?.vaData.segmentationDetails.imageSize.height!
          );
        }
        context.closePath();

        if (showContour) {
          context.stroke();
        }

        if (showArea) {
          context.fill();
        }

        if (showBox) {
          const minX =
            (Math.min(...segmentations[i].coordinates.map((coord) => coord.x)) * videoPlayerWidth) /
            videoAnalysis?.vaData.segmentationDetails.imageSize.width!;
          const maxX =
            (Math.max(...segmentations[i].coordinates.map((coord) => coord.x)) * videoPlayerWidth) /
            videoAnalysis?.vaData.segmentationDetails.imageSize.width!;
          const minY =
            (Math.min(...segmentations[i].coordinates.map((coord) => coord.y)) *
              videoPlayerHeight) /
            videoAnalysis?.vaData.segmentationDetails.imageSize.height!;
          const maxY =
            (Math.max(...segmentations[i].coordinates.map((coord) => coord.y)) *
              videoPlayerHeight) /
            videoAnalysis?.vaData.segmentationDetails.imageSize.height!;

          context.strokeRect(minX, minY, maxX - minX, maxY - minY);
        }
      }
    },
    [showArea, showBox, showContour, videoAnalysis, videoPlayerHeight, videoPlayerWidth]
  );

  const detectedGroupCodes = JSON.stringify(videoAnalysis?.detectedGroupCodes);

  const renderClosestPredictionBasedOnVideoTime = useCallback(
    (context: CanvasRenderingContext2D, currentVideoTime: number) => {
      context!.clearRect(0, 0, videoPlayerWidth, videoPlayerHeight);

      const CLOSEST_PREDICTION_THRESHOLD = 0.15;
      const closestSegmentations = filteredSegmentations?.filter((segmentation) => {
        const closestTimestamp = filteredSegmentations?.reduce((previous, current) =>
          Math.abs(current.timestamp - currentVideoTime) <
          Math.abs(previous.timestamp - currentVideoTime)
            ? current
            : previous
        )?.timestamp;

        return segmentation.timestamp === closestTimestamp;
      });

      if (
        !closestSegmentations ||
        !closestSegmentations.length ||
        Math.abs(closestSegmentations[0].timestamp - currentVideoTime) >
          CLOSEST_PREDICTION_THRESHOLD
      ) {
        return;
      }

      videoAnalysis?.detectedGroupCodes?.forEach((groupCode) => {
        if (groupCode.isVisible && groupCode.isDetected) {
          const groupCodeClosestSegmentations = closestSegmentations?.filter(
            (s) => s.groupCode === groupCode.code
          );

          if (groupCodeClosestSegmentations?.length) {
            renderSegmentationsBasedOnGroupCode(groupCodeClosestSegmentations, context!);
          }
        }
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      renderSegmentationsBasedOnGroupCode,
      videoAnalysis?.getGroupCodeColors,
      detectedGroupCodes,
      videoPlayerHeight,
      videoPlayerWidth,
      filteredSegmentations,
    ]
  );

  useEffect(() => {
    if (!featureFlagsStorage.isFeatureEnabled(Flags.Show_Video_Segmentations)) return;

    const canvas = canvasRef.current;
    const context = canvas?.getContext("2d");
    const videoElement = document.querySelector("#my-video > video") as any;

    if (!context || videoAnalysis?.isLoading || !videoElement) return;

    const renderFramePredictions = () => {
      const computedWidth =
        (videoElement?.clientHeight * videoElement.videoWidth) / videoElement.videoHeight;

      setVideoPlayerWidth(computedWidth);
      setVideoPlayerHeight(videoElement?.clientHeight);
      if (videoAnalysis?.segmentationInProgress || videoAnalysis?.analysisInProgress) {
        context!.clearRect(0, 0, videoPlayerWidth, videoPlayerHeight);
        return;
      }

      if (videoAnalysis?.vaData.segmentationDetails && filteredSegmentations) {
        if (!videoPlayerService.isVideoPlaying) {
          renderClosestPredictionBasedOnVideoTime(context, videoPlayerService.videoProgress);
          return;
        }
        renderClosestPredictionBasedOnVideoTime(context, videoElement.currentTime);

        requestId.current = requestAnimationFrame(renderFramePredictions);
      }
    };

    renderFramePredictions();

    return () => {
      cancelAnimationFrame(requestId.current!);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFullscreen, videoAnalysis?.isLoading, videoAnalysis?.analysisInProgress, videoAnalysis?.segmentationInProgress, videoProgress, showArea, showContour, showBox]);

  return (
    <div
      ref={player}
      className={themeClass(["video-player"], [isPictureInPicture ? "detached-mode" : ""])}
      style={{ width: "auto", height: "auto" }}
    >
      {!videoCanPlay && <LoadingSpinner spinnerOnly />}
      {videoCanPlay && (
        <>
          {featureFlagsStorage.isFeatureEnabled(Flags.Show_Video_Segmentations) && (
            <canvas
              style={{ position: "absolute" }}
              width={videoPlayerWidth}
              height={videoPlayerHeight}
              ref={canvasRef}
            />
          )}
          <ReactPlayer
            id={"my-video"}
            ref={video}
            url={videoUrlWithInitialProgress}
            playing={isVideoPlaying}
            width={videoWidth}
            height={videoHeight}
            onProgress={handleProgress}
            onDuration={handleDuration}
            onDisablePIP={cancelDetachVideo}
          />
          <VideoControls
            videoAnalysis={props.videoAnalysis}
            filteredSegmentations={filteredSegmentations}
          />
          {isPictureInPicture && (
            <div className="video-player-detached-text">
              {translationService.translate("damages.DamagesVideo.video-is-detached")}
            </div>
          )}
        </>
      )}
    </div>
  );
});
